import { tokenCreate } from '$lib/stores/user/action';
import { user } from '$lib/stores/user/store';
import { app } from '$lib/stores/app/store';
import { get } from 'svelte/store';
import {
	AOFL_PRODUCT_UUIDS,
	OUTBOUND_PATH,
	PAGES,
	SENTRY_ERROR_LEVELS
} from '$lib/utils/constants';
import { getDeviceId } from '$lib/utils/device-id';
import { isMobileViewer, startLoading, stopLoading } from '$lib/stores/app/actions';
import { Sentry } from '../sentry';
import { AnalyticsService } from '$lib/services/analytics';
import { z } from 'zod';
const WEB_APP_CREATE_CHILD_ACCOUNT_PATH = '/onboarding/create-child-account';

/**
 * Zod schema for validating authentication tokens
 * Ensures token is a non-empty string after trimming whitespace
 */
const TokenSchema = z.string().trim().min(1, 'Token must be a non-empty string');

/**
 * FlutterAppService handles navigation to the Flutter 2.0 web application.
 *
 * This service manages authentication tokens and provides a simple interface
 * for navigating from Svelte pages to the Flutter app with proper authentication.
 */
class FlutterAppService {
	/**
	 * Validates that a token is a non-empty string using Zod schema
	 * @param {string} token - The token to validate
	 * @returns {boolean} True if token is valid, false otherwise
	 */
	static validateToken(token) {
		const result = TokenSchema.safeParse(token);
		return result.success;
	}

	/**
	 * Validates that a URL is properly formatted
	 * @param {string} url - The URL to validate
	 * @returns {boolean} True if URL is valid, false otherwise
	 */
	static validateUrl(url) {
		try {
			new URL(url);
			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Creates a token
	 * @returns {Promise<string>} The created token
	 * @throws {Error} When token creation fails
	 */
	static async createToken() {
		const response = await tokenCreate(AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0);

		// Check for errors first
		if (response.error) {
			throw new Error(`Token creation failed: ${response.error.message || 'Unknown error'}`);
		}

		// Validate token exists and is valid
		if (!response.data?.token || !this.validateToken(response.data.token)) {
			throw new Error('Token creation succeeded but invalid token returned');
		}

		return response.data.token;
	}

	/**
	 * Navigates to the Flutter app (native or web based on device type).
	 * Automatically determines whether to navigate to native app or web app
	 * based on whether the user is on a mobile device.
	 * @returns {Promise<void>}
	 * @throws {Error} When navigation fails (errors are caught and logged to Sentry)
	 */
	static async navigate() {
		startLoading();
		try {
			if (isMobileViewer()) {
				await this.navigateToNativeApp();
			} else {
				await this.navigateToWebApp();
			}
		} catch (error) {
			Sentry.captureMessage('Error: Navigate to Flutter App', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: error,
				}
			});
		} finally {
			stopLoading();
		}
	}

	/**
	 * Ensures we have a valid token, creating one if needed.
	 * @returns {Promise<string>} The token to use
	 * @throws {Error} When unable to create or retrieve a valid token
	 */
	static async ensureToken() {
		// If we have a valid session, create a short-lived token
		if (get(user).tokenValid) {
			return await this.createToken('ensureToken');
		}

		// No valid session - cannot create token without authentication
		throw new Error('Unable to obtain authentication token: No valid session');
	}

	/**
	 * Navigates to the native Flutter app with authentication token
	 * @returns {Promise<void>}
	 * @throws {Error} When token is invalid or URL construction fails
	 */
	static async navigateToNativeApp() {
		const token = await this.ensureToken();
		if (!this.validateToken(token)) {
			throw new Error('Invalid token for native app navigation');
		}

		const appMeta = get(app).meta;
		const baseUrl = appMeta.isAmazonViewer
			? OUTBOUND_PATH.LINK_TO_APP_2_0_AMAZON
			: OUTBOUND_PATH.LINK_TO_APP_2_0;

		const destinationUrl = `${baseUrl}${token}`;

		// Validate URL before navigation
		if (!this.validateUrl(destinationUrl)) {
			throw new Error(`Invalid URL constructed for native app: ${destinationUrl}`);
		}

		AnalyticsService.trackClientDebug({
			eventSubType: 'mkt-exit-event',
			source: 'FlutterAppService: navigateToNativeApp',
			destination: destinationUrl
		});
		window.location.href = destinationUrl;
	}

	/**
	 * Builds the web app URL with the provided token
	 * @param {string} providedToken - The authentication token
	 * @param {string} redirectPath - Optional redirect path (defaults to create child account)
	 * @returns {Promise<string>} The web app URL
	 * @throws {Error} When token is invalid or URL construction fails
	 */
	static async buildWebAppUrl(
		providedToken = '',
		redirectPath = WEB_APP_CREATE_CHILD_ACCOUNT_PATH
	) {
		const token = providedToken || (await this.ensureToken());
		if (!this.validateToken(token)) {
			throw new Error('Invalid token provided to buildWebAppUrl');
		}

		const deviceId = getDeviceId();
		const params = new URLSearchParams();
		params.append('device_id', deviceId);
		params.append('redirect_path', redirectPath);

		const url = `${PAGES.EXTERNAL.ABCMOUSE_2_0}/auth/${token}?${params.toString()}`;

		// Validate URL format
		if (!this.validateUrl(url)) {
			throw new Error(`Invalid URL constructed: ${url}`);
		}

		return url;
	}

	/**
	 * Navigates to the Flutter web app with authentication token
	 * @param {string} providedToken - The authentication token
	 * @returns {Promise<void>}
	 */
	static async navigateToWebApp(providedToken = '') {
		const url = await this.buildWebAppUrl(providedToken);
		AnalyticsService.trackClientDebug({
			eventSubType: 'mkt-exit-event',
			source: 'FlutterAppService: navigateToWebApp',
			destination: url
		});
		window.location.href = url;
	}
}

export { FlutterAppService };
